/**
 * iter-170o — useResourceCap hook
 *
 * Single-source of truth for per-feature allowance state in the UI.
 * Reads from ``GET /api/billing/my-caps-usage`` (cached via React
 * Query, 60s stale) so every component renders consistent numbers.
 *
 * Returns:
 *   {
 *     limit, used, remaining, unlimited,
 *     atSoftLimit,   // >= 80% of cap used
 *     atHardLimit,   // remaining === 0 (and !unlimited)
 *     loading, error,
 *   }
 *
 * Usage:
 *   const cap = useResourceCap("cash_accounts");
 *   <Button disabled={cap.atHardLimit}>Nueva cuenta</Button>
 *   <span>{cap.used}/{cap.limit} usadas</span>
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../lib/api";

const SOFT_LIMIT_THRESHOLD = 0.8;

export function useResourceCap(resourceKey) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing-my-caps-usage"],
    queryFn: async () => (await api.get("/billing/my-caps-usage")).data,
    staleTime: 60_000,
  });

  return useMemo(() => {
    const row = data?.resources?.[resourceKey];
    if (!row) {
      // Dev-mode warning: unknown resourceKey should not silently render
      // an enabled button — usually a typo or a stale catalog reference.
      if (
        !isLoading &&
        data?.resources &&
        process.env.NODE_ENV !== "production"
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[useResourceCap] Unknown resourceKey "${resourceKey}". Known keys: ${Object.keys(
            data.resources,
          ).join(", ")}`,
        );
      }
      return {
        limit: 0,
        used: 0,
        remaining: 0,
        unlimited: false,
        atSoftLimit: false,
        atHardLimit: false,
        loading: isLoading,
        error,
      };
    }
    const { limit, used, remaining, unlimited, kind } = row;
    const usage_ratio = !unlimited && limit > 0 ? used / limit : 0;
    return {
      limit,
      used,
      remaining,
      unlimited,
      kind,
      atSoftLimit: !unlimited && usage_ratio >= SOFT_LIMIT_THRESHOLD,
      atHardLimit: !unlimited && remaining === 0,
      loading: isLoading,
      error,
    };
  }, [data, isLoading, error, resourceKey]);
}
